<template>
    <div class="chat-container">
        <!-- Header with ticket info -->
        <div class="chat-header">
            <div v-if="ticket" class="d-flex justify-space-between align-center">
                <div class="d-flex align-center">
                    <v-btn
                        icon
                        size="small"
                        variant="text"
                        color="white"
                        class="mr-2"
                        :title="$t('messages.refresh_messages')"
                        @click="refreshMessages"
                    >
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                    <div>
                        <div class="text-white">{{ ticket.subject }}</div>
                        <div class="text-caption text-white">#{{ ticket.id }} · {{ formatDate(ticket.createdAt) }}</div>
                    </div>
                </div>

                <div>
                    <v-btn
                        v-if="ticket.status !== 'closed' && isTO1User"
                        color="error"
                        size="small"
                        class="ml-2"
                        @click="closeTicket"
                    >
                        <v-icon start>mdi-close</v-icon>
                        {{ $t('messages.close_ticket') }}
                    </v-btn>
                    <v-btn
                        v-else-if="ticket.status === 'closed' && isTO1User"
                        color="success"
                        size="small"
                        class="ml-2"
                        @click="reopenTicket"
                    >
                        <v-icon start>mdi-refresh</v-icon>
                        {{ $t('messages.reopen_ticket') }}
                    </v-btn>
                </div>
            </div>
            <div v-else class="text-body-1">
                <span>{{ $t('messages.select_ticket_to_view_messages') }}</span>
            </div>
        </div>

        <!-- No ticket selected state -->
        <div v-if="!ticket" class="chat-empty-state">
            <v-icon color="var(--zsh-primary)" size="64">mdi-chat-outline</v-icon>
            <div class="text-body-1 mt-3">{{ $t('messages.select_ticket_to_view_conversation') }}</div>
        </div>

        <div v-else class="chat-content">
            <!-- Messages area -->
            <div ref="messagesContainer" class="chat-messages">
                <div v-if="isLoadingMessages" class="d-flex align-center justify-center h-100">
                    <v-progress-circular indeterminate color="var(--zsh-primary)" class="mr-3" />
                    <span>{{ $t('messages.loading_messages') }}</span>
                </div>

                <div v-else-if="messagesError" class="my-4">
                    <v-alert type="error" density="compact">
                        {{ messagesError }}
                    </v-alert>
                </div>

                <div v-else-if="messages.length === 0" class="d-flex flex-column align-center justify-center h-100">
                    <v-icon color="var(--zsh-primary)" size="48">mdi-chat-outline</v-icon>
                    <div class="mt-2">{{ $t('messages.no_messages_yet') }}</div>
                    <div class="text-caption">{{ $t('messages.start_conversation') }}</div>
                </div>

                <div v-else class="messages-list">
                    <!-- Participants legend (show if more than 2 participants) -->
                    <div v-if="uniqueParticipants.size > 2" class="participants-legend mb-4">
                        <div class="text-caption mb-1">Participants:</div>
                        <div class="d-flex flex-wrap">
                            <div
                                v-for="[id, participant] in uniqueParticipants.entries()"
                                :key="id"
                                class="participant-chip mr-2 mb-1"
                            >
                                <v-avatar size="20" :color="participant.color" class="mr-1">
                                    <span class="text-caption text-white">{{ getInitials(participant.name) }}</span>
                                </v-avatar>
                                <span class="text-caption">{{ participant.name }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- All Messages -->
                    <v-card
                        v-for="(message, index) in messages"
                        :key="index"
                        class="message-card rounded elevation-1"
                        :class="{'ticket-author-message': message.isTicketAuthor}"
                        :style="{
                            borderLeft: `4px solid ${getParticipantColor(message.senderId, message.sender)}`,
                            backgroundColor: message.isTicketAuthor
                                ? '#f5f5f5'
                                : message.isFromUser
                                  ? '#e0f2f1'
                                  : 'white',
                        }"
                    >
                        <v-card-text>
                            <div class="d-flex align-center mb-1">
                                <v-avatar
                                    size="28"
                                    :color="getParticipantColor(message.senderId, message.sender)"
                                    class="mr-2"
                                >
                                    <span v-if="uniqueParticipants.size > 2" class="text-white text-caption">
                                        {{ getInitials(message.sender) }}
                                    </span>
                                    <v-icon v-else size="small" color="white">
                                        {{ getParticipantIcon(message.senderId) }}
                                    </v-icon>
                                </v-avatar>
                                <div>
                                    <strong :style="{color: getParticipantColor(message.senderId, message.sender)}">
                                        {{ message.sender }}
                                    </strong>
                                    <v-chip
                                        v-if="message.isTicketAuthor"
                                        size="x-small"
                                        color="teal"
                                        text-color="white"
                                        class="ml-2"
                                        density="compact"
                                    >
                                        Ticket Author
                                    </v-chip>
                                </div>
                                <v-spacer />
                                <span class="text-caption">{{ formatDate(message.timestamp) }}</span>
                            </div>
                            <div v-html="message.text" />
                        </v-card-text>
                    </v-card>
                </div>
            </div>

            <!-- Message input area -->
            <div v-if="ticket && ticket.status !== 'closed'" class="chat-input">
                <v-textarea
                    v-model="newMessage"
                    :placeholder="$t('messages.type_your_message')"
                    rows="2"
                    auto-grow
                    hide-details
                    variant="outlined"
                    bg-color="white"
                    density="comfortable"
                    class="flex-grow-1 mr-2"
                    @keydown.enter.prevent="sendMessage"
                />
                <v-btn
                    color="teal"
                    :disabled="!newMessage.trim() || isSendingMessage"
                    class="align-self-end mb-1"
                    @click="sendMessage"
                >
                    <v-icon v-if="isSendingMessage">
                        <v-progress-circular indeterminate size="20" width="2" />
                    </v-icon>
                    <v-icon v-else>mdi-send</v-icon>
                </v-btn>
            </div>

            <div v-else-if="ticket && ticket.status === 'closed'" class="chat-closed-notice">
                <span
                    >{{ $t('messages.ticket_closed') }}
                    {{ isTO1User ? $t('messages.ticket_closed_reopen') : $t('messages.ticket_closed_to1_only') }}</span
                >
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue, Watch} from 'vue-facing-decorator'
    import {format} from 'date-fns'
    import {authStore} from '@/composables/store-accessor'
    import {maskSupportName, maskSupportNameInText} from '../../utils/messaging-utils'

    interface Ticket {
        id: string
        subject: string
        status: string
        createdAt: number
        updatedAt: number
        companyId: string
        cachedMessages?: Message[]
    }

    interface Message {
        id: string
        text: string
        sender: string
        timestamp: number
        isFromUser: boolean
        senderId: string // Unique identifier for the participant
        senderExternalId?: string // External ID mapping to app user ID
        isTicketAuthor?: boolean // Flag to identify the ticket author
    }

    @Component
    export default class TicketChat extends Vue {
        @Prop() ticket!: Ticket | null

        messages: Message[] = []
        newMessage: string = ''
        isLoadingMessages: boolean = false
        isSendingMessage: boolean = false
        messagesError: string | null = null
        uniqueParticipants: Map<string, {color: string; name: string}> = new Map()

        // Computed property to check if the current user has TO1 role
        get isTO1User(): boolean {
            if (!authStore.person || !authStore.person.role) {
                return false
            }

            const role = authStore.person.role
            return (
                role.includes('to1') ||
                role.includes('TO1') ||
                role === 'director' ||
                role === 'supervisor' ||
                role === 'support'
            )
        }

        // Predefined colors for participants (modern messenger palette)
        participantColors: string[] = [
            'teal',
            'purple',
            'indigo',
            'deep-purple',
            'blue',
            'light-blue',
            'cyan',
            'green',
            'light-green',
            'amber',
            'orange',
            'deep-orange',
        ]

        @Watch('ticket')
        onTicketChange() {
            if (this.ticket) {
                this.loadMessages()
            } else {
                this.messages = []
            }
        }

        async loadMessages() {
            if (!this.ticket) return

            // Reset participants map when loading new messages
            this.uniqueParticipants.clear()

            // Use cached messages if available
            if (this.ticket.cachedMessages) {
                this.messages = this.ticket.cachedMessages
                this.$nextTick(() => {
                    this.scrollToBottom()
                })
                return
            }

            this.isLoadingMessages = true
            this.messagesError = null

            try {
                const token = useCookie('token').value

                if (!token) {
                    throw new Error(this.$t('messages.authentication_token_not_found'))
                }

                // Get messages for the selected ticket using the proxy endpoint
                const response = await fetch(`/proxy/tickets/${this.ticket.id}/messages`, {
                    headers: {
                        Accept: 'application/json',
                        Token: token,
                    },
                })

                if (!response.ok) {
                    throw new Error(`Error: ${response.status} - ${response.statusText}`)
                }

                const data = await response.json()

                // Log the messages received from the server
                if (data.messages && data.messages.length > 0) {
                }

                // Process messages
                const messages = data.messages || []

                // Get the ticket author ID from the response
                // This is more reliable than assuming the first message is from the ticket author
                const ticketAuthorId = data.ticketAuthorId

                // Process messages and identify the ticket author's messages
                this.messages = messages.map((msg, index) => {
                    // Check if this message is from the ticket author
                    const isAuthorMessage = ticketAuthorId && msg.senderId === ticketAuthorId

                    // Log the first message for debugging
                    if (index === 0) {
                    }

                    // If this is the default description message, ensure it's marked as the ticket author's message
                    const isDefaultDescription = msg.id === `${this.ticket?.id}-default-description`
                    if (isDefaultDescription) {
                    }

                    return {
                        id: msg.id,
                        text: maskSupportNameInText(msg.content),
                        sender: maskSupportName(msg.senderName),
                        timestamp: msg.createdAt,
                        isFromUser: msg.senderId === authStore.person?.personId,
                        senderId: msg.senderId,
                        senderExternalId: msg.senderExternalId,
                        isTicketAuthor: isAuthorMessage, // Mark messages from the ticket author
                    }
                })

                // Log the processed messages

                // Scroll to bottom of messages
                this.$nextTick(() => {
                    this.scrollToBottom()
                })
            } catch (error) {
                console.error('Error loading messages:', error)
                this.messagesError = this.$t('messages.failed_to_load_messages')
            } finally {
                this.isLoadingMessages = false
            }
        }

        async sendMessage() {
            if (!this.ticket || !this.newMessage.trim()) return

            this.isSendingMessage = true

            try {
                const token = useCookie('token').value

                if (!token) {
                    throw new Error(this.$t('messages.authentication_token_not_found'))
                }

                // Add message to UI immediately for better UX
                const tempId = `temp-${Date.now()}`
                const senderName = `${authStore.person?.firstName || ''} ${authStore.person?.lastName || ''}`.trim()
                const senderId = authStore.person?.personId || 'current-user'

                const newMsg: Message = {
                    id: tempId,
                    text: this.newMessage,
                    sender: maskSupportName(senderName),
                    timestamp: Date.now(),
                    isFromUser: true,
                    senderId: senderId,
                    isTicketAuthor: false, // New messages are never the ticket author message
                }

                this.messages.push(newMsg)

                // Scroll to bottom
                this.$nextTick(() => {
                    this.scrollToBottom()
                })

                // Clear input
                const messageText = this.newMessage
                this.newMessage = ''

                // Log detailed user information for debugging

                // Check if email is missing and log a warning
                if (!authStore.person?.email) {
                    console.warn('WARNING: User email is missing from authStore.person!')
                }

                // Send to API using the proxy endpoint
                const response = await fetch(`/proxy/tickets/${this.ticket.id}/messages`, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Token: token,
                    },
                    body: JSON.stringify({
                        content: messageText,
                        senderId: authStore.person?.personId,
                        senderName: `${authStore.person?.firstName || ''} ${authStore.person?.lastName || ''}`.trim(),
                        userEmail: authStore.person?.email, // Add user email to the request payload
                    }),
                })

                if (!response.ok) {
                    throw new Error(`Error: ${response.status} - ${response.statusText}`)
                }

                // Reload messages to get server-generated ID and timestamp
                await this.loadMessages()

                // Emit event to parent component to refresh ticket data (for highlighting updates)
                this.$emit('message-sent', this.ticket)
            } catch (error) {
                console.error('Error sending message:', error)
                // Show error toast or notification
                alert(this.$t('messages.send_message_failed'))
            } finally {
                this.isSendingMessage = false
            }
        }

        async closeTicket() {
            if (!this.ticket) return

            // Check if user has TO1 role
            if (!this.isTO1User) {
                console.error('Permission denied: Only TO1 role users can close tickets')
                alert(this.$t('messages.permission_denied_close'))
                return
            }

            try {
                const token = useCookie('token').value

                if (!token) {
                    throw new Error(this.$t('messages.authentication_token_not_found'))
                }

                // Update ticket status using the proxy endpoint
                const response = await fetch(`/proxy/tickets/${this.ticket.id}`, {
                    method: 'PUT',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Token: token,
                    },
                    body: JSON.stringify({
                        status: 'closed',
                    }),
                })

                if (!response.ok) {
                    throw new Error(`Error: ${response.status} - ${response.statusText}`)
                }

                // Update local ticket status
                this.ticket.status = 'closed'

                // Emit event to parent component
                this.$emit('ticket-updated', this.ticket)
            } catch (error) {
                console.error('Error closing ticket:', error)
                alert(this.$t('messages.close_ticket_failed'))
            }
        }

        async reopenTicket() {
            if (!this.ticket) return

            // Check if user has TO1 role
            if (!this.isTO1User) {
                console.error('Permission denied: Only TO1 role users can reopen tickets')
                alert(this.$t('messages.permission_denied_reopen'))
                return
            }

            try {
                const token = useCookie('token').value

                if (!token) {
                    throw new Error(this.$t('messages.authentication_token_not_found'))
                }

                // Update ticket status using the proxy endpoint
                const response = await fetch(`/proxy/tickets/${this.ticket.id}`, {
                    method: 'PUT',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Token: token,
                    },
                    body: JSON.stringify({
                        status: 'open',
                    }),
                })

                if (!response.ok) {
                    throw new Error(`Error: ${response.status} - ${response.statusText}`)
                }

                // Update local ticket status
                this.ticket.status = 'open'

                // Emit event to parent component
                this.$emit('ticket-updated', this.ticket)
            } catch (error) {
                console.error('Error reopening ticket:', error)
                alert(this.$t('messages.reopen_ticket_failed'))
            }
        }

        refreshMessages() {
            if (this.ticket) {
                this.ticket.cachedMessages = undefined
            }
            this.loadMessages()
        }

        scrollToBottom() {
            const container = this.$refs.messagesContainer as HTMLElement
            if (container) {
                container.scrollTop = container.scrollHeight
            }
        }

        formatDate(timestamp: number): string {
            return format(new Date(timestamp), 'MMM d, yyyy h:mm a')
        }

        getStatusColor(status: string): string {
            switch (status.toLowerCase()) {
                case 'open':
                    return 'green'
                case 'pending':
                    return 'orange'
                case 'closed':
                    return 'grey'
                default:
                    return 'blue'
            }
        }

        getStatusIcon(status: string): string {
            switch (status.toLowerCase()) {
                case 'open':
                    return 'mdi-ticket'
                case 'pending':
                    return 'mdi-ticket-account'
                case 'closed':
                    return 'mdi-ticket-confirmation'
                default:
                    return 'mdi-ticket-outline'
            }
        }

        // Get or assign a color for a participant
        getParticipantColor(senderId: string, senderName: string): string {
            // If we already have this participant, return their assigned color
            if (this.uniqueParticipants.has(senderId)) {
                return this.uniqueParticipants.get(senderId)!.color
            }

            // For current user, always use teal
            if (senderId === authStore.person?.personId) {
                this.uniqueParticipants.set(senderId, {
                    color: 'teal',
                    name: senderName,
                })
                return 'teal'
            }

            // Assign a new color from our palette
            const colorIndex = this.uniqueParticipants.size % this.participantColors.length
            const color = this.participantColors[colorIndex]

            this.uniqueParticipants.set(senderId, {
                color,
                name: senderName,
            })

            return color
        }

        // Get participant icon based on type
        getParticipantIcon(senderId: string): string {
            // Current user
            if (senderId === authStore.person?.personId) {
                return 'mdi-account'
            }

            // Admin/support
            if (senderId === 'admin') {
                return 'mdi-headset'
            }

            // Default for other participants
            return 'mdi-account-outline'
        }

        // Get initials from sender name
        getInitials(name: string): string {
            if (!name) return '?'

            const parts = name.split(' ')
            if (parts.length === 1) {
                return parts[0].charAt(0).toUpperCase()
            }

            return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
        }
    }
</script>

<style scoped>
    .chat-container {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100%; /* Ensure full width */
        background-color: var(--zsh-messages-panel-bg);
        border-radius: 4px;
        color: var(--zsh-messages-text);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        border: 1px solid var(--zsh-messages-border);
        min-height: 0; /* Crucial for flex items to respect parent container boundaries */
        flex: 1;
        overflow: hidden; /* Prevent overflow */
    }

    .chat-header {
        flex: 0 0 auto;
        height: 120px;
        padding: 16px 16px 8px 16px;
        border-bottom: 1px solid var(--zsh-messages-border);
        background-color: var(--zsh-primary);
        color: white;
        border-radius: 4px 4px 0 0;
    }

    .chat-empty-state {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 0; /* Crucial for flex items to respect parent container boundaries */
        overflow: hidden; /* Prevent overflow */
    }

    .chat-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        margin-top: 8px; /* Add space after header */
        min-height: 0; /* Crucial for flex items to respect parent container boundaries */
    }

    .chat-messages {
        flex: 1;
        overflow-y: auto;
        padding: 20px;
        background-color: white;
        margin: 16px;
        border-radius: 8px;
        box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.05);
        border: 1px solid rgba(0, 0, 0, 0.05);
        max-height: 100%; /* Use relative height instead of viewport-based calculation */
    }

    .chat-input {
        flex: 0 0 auto;
        display: flex;
        padding: 0 16px 16px 16px;
        margin-top: 8px;
    }

    .chat-closed-notice {
        flex: 0 0 auto;
        text-align: center;
        padding: 12px;
        margin: 0 16px 16px 16px;
        background-color: rgba(0, 0, 0, 0.05);
        border-radius: 4px;
        color: var(--zsh-messages-text);
    }

    /* Message styling */
    .message-card {
        transition: all 0.2s ease;
        margin-bottom: 32px !important; /* Increase vertical spacing between messages */
        width: 100% !important; /* Make messages full width */
    }

    /* Add some styling for HTML content in messages */
    .message-card :deep(a) {
        color: #2196f3;
        text-decoration: underline;
    }

    .message-card :deep(p) {
        margin-bottom: 8px;
    }

    .message-card :deep(ul),
    .message-card :deep(ol) {
        padding-left: 20px;
        margin-bottom: 8px;
    }

    .message-card :deep(img) {
        max-width: 100%;
        height: auto;
        margin: 8px 0;
    }

    /* Participant styling */
    .participants-legend {
        background-color: rgba(0, 0, 0, 0.03);
        border-radius: 8px;
        padding: 8px 12px;
        border: 1px solid rgba(0, 0, 0, 0.05);
    }

    .participant-chip {
        display: flex;
        align-items: center;
        background-color: rgba(0, 0, 0, 0.03);
        border-radius: 16px;
        padding: 2px 8px 2px 2px;
    }

    /* Message styling for visual differentiation */
    .message-card {
        position: relative;
        border-radius: 8px !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
        border: 1px solid rgba(0, 0, 0, 0.1) !important;
    }

    /* Add more padding to message content */
    .message-card .v-card-text {
        padding: 16px !important;
    }

    /* Special styling for ticket author message */
    .ticket-author-message {
        box-shadow: 0 3px 5px rgba(0, 0, 0, 0.15) !important;
        border-top: 2px solid teal !important;
        position: relative;
    }

    /* Add a small indicator above the ticket author message */
    .ticket-author-message::before {
        content: '';
        position: absolute;
        top: -8px;
        left: 16px;
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-bottom: 8px solid teal;
    }
</style>
